/**
 * ClinicalDisclaimer — Maldek OS v1.1 § 12-13.
 *
 * Mandatory footer rendered on every page. Maldek is positioned as
 * a clinical-decision-SUPPORT system and educational tool — it does not
 * diagnose, prescribe, or replace licensed clinical judgment.
 *
 * Mounted globally in App.js so it appears on every route — including
 * AI-generated outputs (which also carry their own inline chip).
 */
import { useState } from "react";

const PRIMARY =
  "This system is for educational and clinical decision-support purposes only. It does not provide medical advice, diagnosis, or treatment.";
const USER_INSTRUCTION =
  "Always follow up with your licensed healthcare provider for any personal medical question. In a life-threatening emergency, call 911 or go to your nearest emergency department.";

export default function ClinicalDisclaimer() {
  const [open, setOpen] = useState(false);
  return (
    <footer
      role="contentinfo"
      data-testid="clinical-disclaimer-footer"
      className="border-t border-[#EAD9BE]/25 bg-white/10/[0.05] text-white/85 mt-auto"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-start sm:items-center gap-3 flex-wrap">
        <p className="text-[11px] sm:text-xs leading-relaxed flex-1 min-w-0">
          <span className="font-mono uppercase tracking-[0.18em] text-[#EAD9BE] mr-2">
            Educational use only
          </span>
          {PRIMARY}{" "}
          <span className="hidden sm:inline">{USER_INSTRUCTION}</span>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="clinical-disclaimer-toggle"
            className="ml-2 text-[#EAD9BE] hover:text-white underline-offset-2 hover:underline text-[11px] font-mono uppercase tracking-widest"
          >
            {open ? "Less" : "Full scope"}
          </button>
          <a
            href="/safety"
            data-testid="footer-safety-link"
            className="ml-3 text-[#D49191] hover:text-white underline-offset-2 hover:underline text-[11px] font-mono uppercase tracking-widest"
          >
            Crisis support
          </a>
          <a
            href="/privacy"
            data-testid="footer-privacy-link"
            className="ml-3 text-white/55 hover:text-white underline-offset-2 hover:underline text-[11px] font-mono uppercase tracking-widest"
          >
            Privacy
          </a>
          <a
            href="/terms"
            data-testid="footer-terms-link"
            className="ml-3 text-white/55 hover:text-white underline-offset-2 hover:underline text-[11px] font-mono uppercase tracking-widest"
          >
            Terms
          </a>
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 -mt-1">
        <p className="text-[10px] text-white/45 leading-relaxed" data-testid="footer-reasoning-attribution-global">
          Adapted with permission from the Clinical Reasoning Loop™, part of the
          Think Like a Provider™ Clinical Reasoning System by Jennawè Whitley,
          APRN, FNP-BC, NP-C. © Capital Covenant Enterprise LLC.
        </p>
      </div>
      {open && (
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 text-[11px] text-white/70 leading-relaxed"
          data-testid="clinical-disclaimer-scope"
        >
          <p className="mb-2 sm:hidden">{USER_INSTRUCTION}</p>
          <p className="mb-1.5 text-white/55 font-mono uppercase tracking-[0.2em] text-[10px]">
            Scope limitations
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            <li>· Not a substitute for professional medical judgment</li>
            <li>· Not a diagnostic authority</li>
            <li>· Not a prescribing authority</li>
            <li>· Not an emergency response system — call 911 for emergencies</li>
          </ul>
          <p className="mt-3 text-white/45 text-[10px] font-mono uppercase tracking-[0.2em]">
            Maldek OS v1.1 · AI in this product is constrained to
            educational explanation, summarization, risk flagging and
            documentation support. AI does not finalize diagnosis or
            initiate irreversible clinical action.
          </p>
        </div>
      )}
    </footer>
  );
}
